import { Typography } from '@mui/material';
import { compareAsc } from 'date-fns';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import TimelineTree from '@components/TimelineTree/index';

import {
  APPOINTMENT_HISTORY,
  EMERGENCY_VISITS,
  HOSPITALIZATIONS,
  IMAGING_TESTS,
  IMMUNIZATIONS,
  LAB_RESULTS_REPORT,
  SURGERIES,
} from '@constants/routes';
import {
  APPOINTMENTS_ID,
  EMERGENCY_VISIT_ID,
  HOSPITALIZATION_ID,
  IMAGING_TESTS_ID,
  IMMUNIZATION_ID,
  LAB_RESULTS_ID,
  SURGERY_ID,
} from '@constants/survey-ids';

import { useIsMobile } from '@hooks/useIsMobile';
import { useNavigate } from '@hooks/useNavigate';
import { useSession } from '@hooks/useSession';

import { NoRecordsWrapper } from '@layouts/Layouts/TableLayout';

import type { GenericRecord } from '@models/generic-record';
import type { TimelineData } from '@models/patient-record';

import { isValidDate } from '@utils/date';
import { getRecordsRange } from '@utils/records';

import { RecordTimelineDiv } from './RecordTimeline.style';
import RecordTimelineBlock from './RecordTimelineBlock/RecordTimelineBlock';

const messages = defineMessages({
  noRecords: {
    id: 'recordsTimeline.noRecords',
    defaultMessage: 'No Records',
  },
  missingRecords: {
    id: 'timelineMain.missingRecords',
    defaultMessage:
      'Some records may be missing a date and might not appear on the timeline',
  },
  allShown: {
    id: 'timelineMain.allShown',
    defaultMessage: 'All Records Shown',
  },
});

interface RecordTimelineProps {
  /**
   * Records to show on the timeline
   */
  data: TimelineData;
  loading?: boolean;
  /**
   * Sort order of records
   */
  order?: 'asc' | 'desc';
}

const RecordTimeline: React.FC<RecordTimelineProps> = ({
  data: {
    appointment,
    diagnostic_report,
    emergency_visits,
    hospitalizations,
    immunization,
    procedure,
  },
  loading = false,
  order = 'asc',
}) => {
  const {
    state: { isAuthenticated, isClient },
  } = useSession();
  const { constructRoute } = useNavigate();

  const surgeriesDisplayData = procedure.map<GenericRecord>((a) => {
    return {
      id: a.id ?? '',
      viewedStatus: a.viewedStatus,
      title: a.procedureName,
      hasNotes: Boolean(a.recordNotes.length),
      facilityCity: a.city ?? '',
      facilityCountry: a.country ?? '',
      hcpName: a.practitioner ?? '',
      facilityName: a.facility ?? '',
      date: a.getProcedureDate(),
      displayDate: a.getProcedureDateStr(),
      route: `${SURGERIES}${a.id}`,
      surveyId: SURGERY_ID,
      selfReported: a.selfReported,
    };
  });

  const emergencyVisitsDisplayData = emergency_visits.map<GenericRecord>(
    (a) => {
      return {
        id: a.id ?? '',
        viewedStatus: a.viewedStatus,
        title: a.visitReason ?? 'ER Visit',
        hasNotes: Boolean(a.recordNotes.length),
        facilityCity: a.city ?? '',
        facilityCountry: a.country ?? '',
        hcpName: a.practitioner ?? '',
        facilityName: a.facility ?? '',
        date: a.getVisitDate(),
        displayDate: a.getVisitDateStr({ includeEnd: false }),
        route: `${EMERGENCY_VISITS}${a.id}`,
        surveyId: EMERGENCY_VISIT_ID,
        selfReported: a.selfReported,
      };
    }
  );

  const hospitalizationsDisplayData = hospitalizations.map<GenericRecord>(
    (a) => {
      return {
        id: a.id ?? '',
        viewedStatus: a.viewedStatus,
        title: a.visitReason ?? 'Hospitalization',
        hasNotes: Boolean(a.recordNotes.length),
        facilityCity: a.city ?? '',
        facilityCountry: a.country ?? '',
        hcpName: a.practitioner ?? '',
        facilityName: a.facility ?? '',
        date: a.getVisitDate(),
        displayDate: a.getVisitDateStr({ includeEnd: false }),
        route: `${HOSPITALIZATIONS}${a.id}`,
        surveyId: HOSPITALIZATION_ID,
        selfReported: a.selfReported,
      };
    }
  );

  const appointmentsDisplayData = appointment.map<GenericRecord>((a) => {
    return {
      id: a.id ?? '',
      viewedStatus: a.viewedStatus,
      title: a.visitReason ?? 'Appointment',
      hasNotes: Boolean(a.recordNotes.length),
      facilityCity: a.facilityCity ?? '',
      facilityCountry: a.facilityCountry ?? '',
      hcpName: a.hcpName ?? '',
      facilityName: a.facilityName ?? '',
      date: a.getVisitDate(),
      displayDate: a.getVisitDateStr(),
      route: `${APPOINTMENT_HISTORY}${a.id}`,
      surveyId: APPOINTMENTS_ID,
      selfReported: a.selfReported,
    };
  });

  const immunizationsDataDisplay = immunization.map<GenericRecord>((a) => {
    return {
      id: a.id ?? '',
      viewedStatus: a.viewedStatus,
      title: a.vaccineName ?? 'Immunization',
      hasNotes: Boolean(a.recordNotes.length),
      facilityCity: a.address ?? '',
      facilityCountry: '',
      hcpName: a.practitioner ?? '',
      facilityName: a.facility ?? '',
      date: a.getImmunizationDate(),
      displayDate: a.getImmunizationDateStr(),
      route: `${IMMUNIZATIONS}`,
      surveyId: IMMUNIZATION_ID,
      selfReported: a.selfReported,
    };
  });

  const labsDisplayData = diagnostic_report
    .filter((d) => d.hasCategory('laboratory'))
    .map<GenericRecord>((a) => {
      const title = a.typeName ?? 'Lab Results';
      return {
        id: a.id ?? '',
        viewedStatus: a.viewedStatus,
        title,
        hasNotes: Boolean(a.recordNotes.length),
        facilityCity: '',
        facilityCountry: a.testLocation ?? '',
        hcpName: a.practitioner ?? '',
        facilityName: a.facilityName ?? '',
        date: a.getTestDate(),
        displayDate: a.getTestDateStr(),
        route: isClient
          ? constructRoute(LAB_RESULTS_REPORT, {
              category: a.code ?? '',
              title,
            })
          : constructRoute(LAB_RESULTS_REPORT, { id: a.id }),
        surveyId: LAB_RESULTS_ID,
        selfReported: a.selfReported,
      };
    });

  const imagingDisplayData = diagnostic_report
    .filter((d) => d.hasCategory('imaging-and-tests'))
    .map<GenericRecord>((a) => {
      return {
        id: a.id ?? '',
        viewedStatus: a.viewedStatus,
        title: a.typeName ?? 'Imaging',
        hasNotes: Boolean(a.recordNotes.length),
        facilityCity: '',
        facilityCountry: a.testLocation ?? '',
        hcpName: a.practitioner ?? '',
        facilityName: a.facilityName ?? '',
        date: a.getTestDate(),
        displayDate: a.getTestDateStr(),
        route: `${IMAGING_TESTS}${a.id}`,
        surveyId: IMAGING_TESTS_ID,
        selfReported: a.selfReported,
      };
    });

  const displayData: GenericRecord[] = emergencyVisitsDisplayData
    .concat(surgeriesDisplayData)
    .concat(appointmentsDisplayData)
    .concat(immunizationsDataDisplay)
    .concat(labsDisplayData)
    .concat(hospitalizationsDisplayData)
    .concat(imagingDisplayData);

  const records = displayData.filter(({ date }) =>
    date ? isValidDate(date) : null
  );

  const { formatMessage } = useIntl();
  const mobile = useIsMobile('md');
  const recordsRange = getRecordsRange(records, undefined, new Date());

  return (
    <ContentLoader loading={loading}>
      <RecordTimelineDiv>
        {Object.keys(recordsRange).length > 0 ? (
          Object.keys(recordsRange)
            .sort((a, b) => {
              if (isValidDate(new Date(a)) && isValidDate(new Date(b))) {
                return b.localeCompare(a);
              }
              if (isValidDate(new Date(a))) {
                return -1;
              }
              if (isValidDate(new Date(b))) {
                return 1;
              }
              return 0;
            })
            .map((year) => {
              const sortedRecords = Object.keys(recordsRange[year])
                .filter(
                  (date) =>
                    recordsRange[year][date] instanceof Object &&
                    Object.keys(recordsRange[year][date]).length !== 0
                )
                .sort((a, b) => {
                  const dateA = new Date(a);
                  const dateB = new Date(b);
                  if (isValidDate(dateA) && isValidDate(dateB)) {
                    return compareAsc(dateA, dateB);
                  }
                  if (isValidDate(dateA)) {
                    return 1;
                  }
                  if (isValidDate(dateB)) {
                    return -1;
                  }
                  return 0;
                });

              if (order === 'desc') {
                sortedRecords.reverse();
              }
              const timelineBlocks = sortedRecords.map((date, childIndex) => (
                <RecordTimelineBlock
                  date={date}
                  flexEnd={childIndex % 2 === 0 && !mobile}
                  key={`timeline-block-${year}-${date}-${childIndex}`}
                  records={recordsRange[year][date]}
                />
              ));
              return (
                <TimelineTree
                  header={year}
                  key={`process-stepper-${year}`}
                  nodes={timelineBlocks}
                />
              );
            })
        ) : (
          <NoRecordsWrapper>
            {formatMessage(messages.noRecords)}
          </NoRecordsWrapper>
        )}
      </RecordTimelineDiv>
      {((isAuthenticated && displayData.length !== records.length) ||
        !isAuthenticated) && (
        <Typography
          color="grey.A700"
          variant={mobile ? 'caption' : 'body2'}
          textAlign="center"
          py={2}
        >
          {formatMessage(
            isAuthenticated ? messages.missingRecords : messages.allShown
          )}
        </Typography>
      )}
    </ContentLoader>
  );
};

export default RecordTimeline;
